import { useTime, interpolate, Easing } from './animations';
import {
  AppShell,
  Avatar,
  ButtonGhost,
  ButtonPrimary,
  Chip,
  Cursor,
  FlagDot,
  PhoneFrame,
  ProgressBar,
  TepLogo,
  Toast,
} from './ui';

// Scenes 1-4: Send placement → Student takes test → Receive results → Add interests
// Each scene is 5s. Uses Stage from animations.jsx and primitives from ui.jsx.

const STUDENT = {
  name: "Mateo Ramírez",
  email: "mateo.r@protonmail.com",
  city: "Madrid, ES",
  country: "ES",
  age: 28,
  l1: "Spanish",
  job: "Architect",
  color: "#7a8c5a",
};

// ────────────────────────────────────────────────────────────────────
// SCENE 1: Send placement assessment
// ────────────────────────────────────────────────────────────────────
function Scene1() {
  const t = useTime();
  // Cursor path: arrives at "Send placement" button → click → confirm → email flies out
  const cursor = {
    x: interpolate([0, 0.6, 1.2, 2.6, 3.6, 5], [820, 700, 660, 660, 540, 540], Easing.easeInOutCubic)(t),
    y: interpolate([0, 0.6, 1.2, 2.6, 3.6, 5], [380, 380, 360, 360, 470, 470], Easing.easeInOutCubic)(t),
  };
  const drawerOpen = t > 1.4;
  const sendClicked = t > 3.0;
  const emailFlight = t > 3.2 && t < 4.6 ? (t - 3.2) / 1.4 : null;
  const toast = t > 4.0;

  return (
    <div style={{ width: 1280, height: 720, background: "#ece6d4", position: "relative" }}>
      <AppShell active="students" breadcrumb="STUDENTS / MATEO RAMÍREZ" title="Mateo Ramírez"
        topRight={<><ButtonGhost icon={<Dot/>}>Activity</ButtonGhost><ButtonPrimary>+ New student</ButtonPrimary></>}>
        <div style={{ padding: "20px 28px", overflow: "hidden", height: "100%", position: "relative" }}>
          {/* Profile header card */}
          <div style={{ background: "#fff", border: "1px solid #e8e3d2", borderRadius: 14, padding: 20, display: "flex", alignItems: "center", gap: 18 }}>
            <Avatar name="Mateo Ramírez" color={STUDENT.color} size={56} ring="#e6ede0"/>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 500 }}>Mateo Ramírez</div>
                <FlagDot country="ES"/>
                <Chip color="cream" size="sm">L1 Spanish</Chip>
                <Chip color="tan" size="sm">Architect</Chip>
              </div>
              <div style={{ fontSize: 12.5, color: "#6b7269", marginTop: 4 }}>Madrid, ES · 28 · Joined Apr 30, 2026 · No assessment yet</div>
            </div>
            <Chip color="cream" size="md" style={{ background: "#f3eedf", color: "#5e4f30" }}>● Awaiting placement</Chip>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 24, marginTop: 18, borderBottom: "1px solid #e8e3d2", padding: "0 4px" }}>
            {["Overview","Pathway","Sessions","Notes","Files"].map((x,i) => (
              <div key={x} style={{
                padding: "10px 0", fontSize: 13, fontWeight: i===0 ? 600 : 500,
                color: i===0 ? "#1f3d2b" : "#6b7269",
                borderBottom: i===0 ? "2px solid #1f3d2b" : "2px solid transparent",
                marginBottom: -1,
              }}>{x}</div>
            ))}
          </div>

          {/* Empty state inviting placement */}
          <div style={{
            marginTop: 22,
            background: "#fff", border: "1px solid #e8e3d2", borderRadius: 14, padding: 28,
            display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "center",
          }}>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#8a8f86", letterSpacing: "0.08em", textTransform: "uppercase" }}>Step 1 of 3</div>
              <div style={{ fontFamily: "var(--display)", fontSize: 26, fontWeight: 500, marginTop: 6, lineHeight: 1.15 }}>Send Mateo a placement assessment</div>
              <div style={{ color: "#6b7269", fontSize: 13.5, marginTop: 8, lineHeight: 1.5, maxWidth: 460 }}>
                Adaptive 22‑question test across reading, listening, grammar &amp; vocabulary. Takes ~25 minutes. Result lands in your inbox the moment they finish.
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <div style={{
                  background: t > 2.6 ? "#162b1e" : "#1f3d2b",
                  color: "#faf6ed", padding: "10px 18px", borderRadius: 8,
                  fontSize: 13.5, fontWeight: 500,
                  display: "inline-flex", alignItems: "center", gap: 8,
                  transform: t > 2.6 && t < 2.9 ? "scale(0.97)" : "scale(1)",
                  transition: "transform 80ms",
                  boxShadow: t > 2.6 ? "inset 0 1px 2px rgba(0,0,0,0.2)" : "none",
                }}>
                  <svg width="14" height="14" viewBox="0 0 14 14"><path d="M2 3h10v8H2z M2 3l5 4 5-4" stroke="#faf6ed" strokeWidth="1.4" fill="none" strokeLinejoin="round"/></svg>
                  Send placement test
                </div>
                <ButtonGhost>Skip — assign manually</ButtonGhost>
              </div>
            </div>
            <div style={{ background: "#faf6ed", border: "1px dashed #d8d2bd", borderRadius: 12, padding: 16 }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, color: "#8a8f86", textTransform: "uppercase", letterSpacing: "0.08em" }}>Test contents</div>
              {[
                ["Listening", "6 questions · ~6 min"],
                ["Reading comprehension", "5 questions · ~5 min"],
                ["Grammar & structure", "8 questions · ~7 min"],
                ["Vocabulary range", "3 questions · ~4 min"],
              ].map((row,i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i<3 ? "1px solid #e8e3d2" : "none", fontSize: 12.5 }}>
                  <span style={{ fontWeight: 500 }}>{row[0]}</span>
                  <span style={{ color: "#8a8f86", fontFamily: "var(--mono)" }}>{row[1]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Slide-out drawer (after click) */}
          {drawerOpen && (
            <div style={{
              position: "absolute", right: 28, top: 130, bottom: 20, width: 380,
              background: "#fff", borderRadius: 14, border: "1px solid #d8d2bd",
              boxShadow: "0 30px 80px -30px rgba(0,0,0,0.35)",
              padding: 22,
              transform: `translateX(${(1 - Math.min(1, (t - 1.4) / 0.45)) * 30}px)`,
              opacity: Math.min(1, (t - 1.4) / 0.45),
              transition: "transform 80ms",
            }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, color: "#8a8f86", textTransform: "uppercase", letterSpacing: "0.08em" }}>New placement</div>
              <div style={{ fontFamily: "var(--display)", fontSize: 19, fontWeight: 500, marginTop: 4 }}>Send to Mateo Ramírez</div>

              <div style={{ marginTop: 18 }}>
                <Label>Recipient</Label>
                <FieldRow><Avatar name="Mateo R" color={STUDENT.color} size={20}/> mateo.r@protonmail.com</FieldRow>

                <Label style={{ marginTop: 14 }}>Test variant</Label>
                <FieldRow><span style={{ fontWeight: 500 }}>General English (CEFR A1–C1)</span><Caret/></FieldRow>

                <Label style={{ marginTop: 14 }}>Deadline</Label>
                <FieldRow>5 days · expires 13 May 2026</FieldRow>

                <Label style={{ marginTop: 14 }}>Personal note</Label>
                <div style={{ border: "1px solid #d8d2bd", borderRadius: 8, padding: "10px 12px", fontSize: 12.5, color: "#2c322d", lineHeight: 1.5, minHeight: 56 }}>
                  Hi Mateo — quick 25‑min test so I can build the right pathway for you. Take it somewhere quiet :)
                </div>
              </div>

              <div style={{ position: "absolute", left: 22, right: 22, bottom: 22, display: "flex", gap: 8 }}>
                <ButtonGhost style={{ flex: 1, justifyContent: "center" }}>Cancel</ButtonGhost>
                <div style={{
                  flex: 2, textAlign: "center",
                  background: sendClicked ? "#162b1e" : "#1f3d2b",
                  color: "#faf6ed", padding: "10px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                }}>
                  Send test
                </div>
              </div>
            </div>
          )}

          {/* Email plane flying from drawer to top-right inbox/student email indicator */}
          {emailFlight !== null && (
            <EmailFlight progress={emailFlight}/>
          )}

          {/* Toast */}
          {toast && (
            <div style={{
              position: "absolute", left: "50%", bottom: 32,
              transform: `translateX(-50%) translateY(${(1 - Math.min(1, (t - 4.0) / 0.4)) * 14}px)`,
              opacity: Math.min(1, (t - 4.0) / 0.4),
            }}>
              <Toast x={0} y={0} color="sage">Placement test sent to Mateo · expires 13 May</Toast>
            </div>
          )}

          <Cursor x={cursor.x} y={cursor.y} label={t > 2.6 && t < 3.2 ? "click" : null}/>
        </div>
      </AppShell>
    </div>
  );
}

function EmailFlight({ progress }) {
  // Curved path from (~870, 540) down-right area to top-right (~1240, 60)
  const startX = 720, startY = 540, endX = 1230, endY = 56;
  const x = startX + (endX - startX) * Easing.easeInOutCubic(progress);
  const arc = -120 * Math.sin(Math.PI * progress);
  const y = startY + (endY - startY) * Easing.easeInOutCubic(progress) + arc;
  return (
    <div style={{
      position: "absolute", left: x, top: y,
      width: 60, height: 44,
      transform: `rotate(${-15 + progress * 30}deg)`,
      filter: "drop-shadow(0 8px 14px rgba(20,30,22,0.25))",
      opacity: progress < 0.95 ? 1 : (1 - (progress - 0.95) * 20),
    }}>
      <svg width="60" height="44" viewBox="0 0 60 44">
        <rect x="2" y="6" width="56" height="34" rx="4" fill="#faf6ed" stroke="#1f3d2b" strokeWidth="1.5"/>
        <path d="M2 10 L30 28 L58 10" stroke="#1f3d2b" strokeWidth="1.5" fill="none"/>
        <rect x="36" y="2" width="22" height="6" fill="#c7b89a"/>
      </svg>
    </div>
  );
}

function Label({ children, style = {} }) {
  return <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "#8a8f86", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, ...style }}>{children}</div>;
}
function FieldRow({ children }) {
  return <div style={{ border: "1px solid #d8d2bd", borderRadius: 8, padding: "9px 12px", fontSize: 12.5, color: "#2c322d", display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>{children}</div>;
}
function Caret() {
  return <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 4l3 3 3-3" stroke="#6b7269" strokeWidth="1.4" fill="none" strokeLinecap="round"/></svg>;
}
function Dot() { return <span style={{ width: 6, height: 6, borderRadius: 6, background: "#7a8c5a", display: "inline-block" }}/>; }


// ────────────────────────────────────────────────────────────────────
// SCENE 2: Student takes the placement assessment (mobile)
// ────────────────────────────────────────────────────────────────────
function Scene2() {
  const t = useTime();
  // Phases (5s total):
  // 0.0-1.0  Q1 listening
  // 1.0-2.0  Q1 answered → Q2 reading appears
  // 2.0-3.0  Q2 answered → Q3 grammar gap-fill
  // 3.0-4.0  Q3 answered → Q4 vocab match
  // 4.0-5.0  Submitted screen
  const phase = t < 1.0 ? 0 : t < 2.0 ? 1 : t < 3.0 ? 2 : t < 4.0 ? 3 : 4;
  const progress = Math.min(1, t / 4.5);

  return (
    <div style={{ width: 1280, height: 720, background: "#ece6d4", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: 60 }}>
      {/* Left side: ambient browser context showing the test page */}
      <div style={{ width: 460, opacity: 0.55, display: "flex", flexDirection: "column", gap: 14, alignItems: "flex-end" }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 32, fontWeight: 500, lineHeight: 1.15, textAlign: "right", maxWidth: 380, color: "#1f3d2b" }}>
          Mateo is taking the placement assessment.
        </div>
        <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#6b7269", letterSpacing: "0.08em", textTransform: "uppercase" }}>Madrid · Wednesday 14:32 CET</div>
        <div style={{ fontSize: 13, color: "#6b7269", textAlign: "right", maxWidth: 360, lineHeight: 1.55 }}>
          Adaptive — questions get harder or easier based on his answers. He sees no time pressure, but the tutor sees realtime progress.
        </div>
      </div>

      {/* Phone */}
      <PhoneFrame width={300} height={620}>
        {/* Status bar */}
        <div style={{ position: "absolute", top: 8, left: 24, fontSize: 10.5, fontFamily: "var(--mono)", color: "#1a1f1c", zIndex: 6, fontWeight: 600 }}>14:32</div>
        <div style={{ position: "absolute", top: 8, right: 22, display: "flex", gap: 4, zIndex: 6 }}>
          <span style={{ fontSize: 9.5, fontFamily: "var(--mono)" }}>5G</span>
          <svg width="14" height="9" viewBox="0 0 14 9"><rect x="0" y="1" width="11" height="7" rx="2" stroke="#1a1f1c" fill="none"/><rect x="1" y="2" width="9" height="5" fill="#1f3d2b"/><rect x="12" y="3" width="1.5" height="3" fill="#1a1f1c"/></svg>
        </div>

        {/* Header */}
        <div style={{ padding: "44px 18px 12px 18px", borderBottom: "1px solid #e8e3d2" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <TepLogo size={14} showWord={false}/>
            <div style={{ fontSize: 11, color: "#6b7269", fontFamily: "var(--mono)" }}>{Math.min(22, Math.floor(t * 4) + 7)}/22</div>
          </div>
          <div style={{ marginTop: 10 }}>
            <ProgressBar value={progress * 100} color="#1f3d2b" track="#e8e3d2" height={4}/>
          </div>
        </div>

        {/* Question content */}
        <div style={{ padding: 20, height: "calc(100% - 96px)", position: "relative", overflow: "hidden" }}>
          {phase === 0 && <QListening t={t} answered={false}/>}
          {phase === 1 && <QReading t={t - 1} answered={t > 1.7}/>}
          {phase === 2 && <QGrammar t={t - 2} answered={t > 2.7}/>}
          {phase === 3 && <QVocab t={t - 3} answered={t > 3.7}/>}
          {phase === 4 && <SubmittedView t={t - 4}/>}
        </div>
      </PhoneFrame>

      {/* Right side: question type indicator */}
      <div style={{ width: 180, display: "flex", flexDirection: "column", gap: 12, opacity: 0.85 }}>
        {[
          { i: 0, label: "Listening" },
          { i: 1, label: "Reading" },
          { i: 2, label: "Grammar" },
          { i: 3, label: "Vocabulary" },
          { i: 4, label: "Submit" },
        ].map(p => (
          <div key={p.i} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 0",
            opacity: phase === p.i ? 1 : 0.35,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: 8,
              background: phase >= p.i ? "#1f3d2b" : "#d8d2bd",
              boxShadow: phase === p.i ? "0 0 0 4px #e6ede0" : "none",
            }}/>
            <div style={{ fontSize: 12.5, fontWeight: phase === p.i ? 600 : 500, color: "#1f3d2b" }}>{p.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QListening({ t }) {
  return (
    <div>
      <Chip color="cream" size="sm">🎧 Listening</Chip>
      <div style={{ marginTop: 14, fontFamily: "var(--display)", fontSize: 17, fontWeight: 500, lineHeight: 1.3 }}>
        Where is the woman going on Saturday?
      </div>
      <div style={{ marginTop: 14, background: "#f3eedf", borderRadius: 12, padding: 14, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 32, background: "#1f3d2b", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 2l7 4-7 4z" fill="#faf6ed"/></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 18 }}>
            {Array.from({length: 26}).map((_,i) => {
              const h = 4 + Math.abs(Math.sin(i * 0.6 + t * 6)) * 14;
              const reached = (i / 26) < t * 1.1;
              return <div key={i} style={{ width: 2, height: h, background: reached ? "#1f3d2b" : "#d8d2bd", borderRadius: 1 }}/>;
            })}
          </div>
          <div style={{ fontSize: 10, color: "#6b7269", fontFamily: "var(--mono)", marginTop: 4 }}>0:14 / 0:32</div>
        </div>
      </div>
      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        {["To her sister's wedding", "To a music festival", "To her parents' house in Bristol", "She isn't going anywhere"].map((opt, i) => (
          <div key={i} style={{
            border: "1px solid #d8d2bd", borderRadius: 10,
            padding: "10px 12px", fontSize: 12.5,
            background: "#fff", color: "#2c322d",
          }}>{String.fromCharCode(65+i)}. {opt}</div>
        ))}
      </div>
    </div>
  );
}

function QReading({ answered }) {
  const correct = 1;
  return (
    <div>
      <Chip color="cream" size="sm">📖 Reading</Chip>
      <div style={{ marginTop: 12, background: "#faf6ed", border: "1px solid #e8e3d2", borderRadius: 10, padding: 12, fontSize: 12, color: "#2c322d", lineHeight: 1.55 }}>
        “The café opens at 7 every morning except Sundays, when Marcus opens at 9 and serves only pastries until lunch.”
      </div>
      <div style={{ marginTop: 12, fontFamily: "var(--display)", fontSize: 15, fontWeight: 500 }}>
        On Sundays the café opens at…
      </div>
      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 7 }}>
        {["7:00", "9:00", "10:00", "It does not say"].map((opt, i) => (
          <div key={i} style={{
            border: `1px solid ${answered && i === correct ? "#1f3d2b" : "#d8d2bd"}`,
            background: answered && i === correct ? "#e6ede0" : "#fff",
            borderRadius: 10, padding: "10px 12px", fontSize: 12.5,
            display: "flex", justifyContent: "space-between",
          }}>
            <span>{String.fromCharCode(65+i)}. {opt}</span>
            {answered && i === correct && <span>✓</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function QGrammar({ t, answered }) {
  const word = "have been";
  const typed = answered ? word : word.slice(0, Math.max(0, Math.floor(t * 12)));
  return (
    <div>
      <Chip color="cream" size="sm">✎ Grammar</Chip>
      <div style={{ marginTop: 14, fontFamily: "var(--display)", fontSize: 16, fontWeight: 500, lineHeight: 1.4 }}>
        Fill in the gap:
      </div>
      <div style={{ marginTop: 12, fontSize: 14, lineHeight: 1.7, color: "#2c322d" }}>
        I&nbsp;
        <span style={{
          display: "inline-block", minWidth: 80,
          borderBottom: "2px solid #1f3d2b",
          padding: "1px 6px", fontWeight: 600, color: "#1f3d2b",
          background: answered ? "#e6ede0" : "transparent",
        }}>{typed}<span className="tep-caret" style={{ color: "#1f3d2b" }}>{!answered && "|"}</span></span>
        &nbsp;working here since 2021.
      </div>
      <div style={{ marginTop: 18 }}>
        <Label>Suggestions</Label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["am", "have been", "was being", "had"].map((s,i) => (
            <Chip key={i} color={s === word && answered ? "ink" : "cream"} size="sm">{s}</Chip>
          ))}
        </div>
      </div>
      <div style={{ position: "absolute", left: 20, right: 20, bottom: 16, fontSize: 10.5, color: "#8a8f86", fontFamily: "var(--mono)", textAlign: "center" }}>
        Q14 · Present perfect · CEFR B1
      </div>
    </div>
  );
}

function QVocab({ t }) {
  const matches = [["punctual", "on time"], ["fluent", "smooth"], ["concise", "short"]];
  return (
    <div>
      <Chip color="cream" size="sm">⌬ Vocabulary</Chip>
      <div style={{ marginTop: 12, fontFamily: "var(--display)", fontSize: 15, fontWeight: 500 }}>
        Match each word to its meaning.
      </div>
      <div style={{ marginTop: 14 }}>
        {matches.map(([a,b],i) => {
          const reveal = t > 0.15 + i * 0.18;
          return (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "9px 0", borderBottom: i<2 ? "1px solid #e8e3d2" : "none",
            }}>
              <div style={{ flex: 1, fontWeight: 600, fontSize: 13, color: "#1f3d2b" }}>{a}</div>
              <svg width="20" height="2"><line x1="0" y1="1" x2="20" y2="1" stroke={reveal ? "#1f3d2b" : "#d8d2bd"} strokeWidth="1.5"/></svg>
              <div style={{ flex: 1, textAlign: "right", fontSize: 12.5, color: reveal ? "#2c322d" : "#b3ad97", transition: "color 200ms" }}>
                {reveal ? b : "—"}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ position: "absolute", left: 20, right: 20, bottom: 16, fontSize: 10.5, color: "#8a8f86", fontFamily: "var(--mono)", textAlign: "center" }}>
        Q21 · Synonyms · CEFR B1+
      </div>
    </div>
  );
}

function SubmittedView({ t }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 16px" }}>
      <div style={{
        width: 64, height: 64, borderRadius: 64,
        background: "#1f3d2b", display: "flex", alignItems: "center", justifyContent: "center",
        transform: `scale(${interpolate([0,0.4,0.8],[0.4, 1.1, 1], Easing.easeOutBack)(t)})`,
      }}>
        <svg width="28" height="28" viewBox="0 0 28 28"><path d="M6 14l5 5 11-12" stroke="#faf6ed" strokeWidth="2.4" fill="none" strokeLinejoin="round" strokeLinecap="round"/></svg>
      </div>
      <div style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 500, marginTop: 16 }}>All done!</div>
      <div style={{ color: "#6b7269", fontSize: 12.5, marginTop: 6, lineHeight: 1.5, maxWidth: 220 }}>
        Your tutor will see your results within a few minutes.
      </div>
      <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "#8a8f86", marginTop: 18, textTransform: "uppercase", letterSpacing: "0.08em" }}>
        22 of 22 · 24:08
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// SCENE 3: Teacher receives results
// ────────────────────────────────────────────────────────────────────
function Scene3() {
  const t = useTime();
  // 0-0.7  notification appears top-right
  // 0.7-1.4 cursor approaches and clicks
  // 1.4-2.0 results panel slides in from right
  // 2.0-3.5 CEFR badge reveals + score animates
  // 3.5-5.0 skill bars animate in stagger
  const cursor = {
    x: interpolate([0, 0.7, 1.3, 2.0, 5], [800, 1100, 1100, 700, 700], Easing.easeInOutCubic)(t),
    y: interpolate([0, 0.7, 1.3, 2.0, 5], [400, 80, 80, 250, 250], Easing.easeInOutCubic)(t),
  };
  const notifShow = t > 0.2 && t < 1.5;
  const panelOpen = t > 1.4;
  const panelP = Math.min(1, Math.max(0, (t - 1.4) / 0.5));

  return (
    <div style={{ width: 1280, height: 720, background: "#ece6d4", position: "relative" }}>
      <AppShell active="students" breadcrumb="STUDENTS" title="My students"
        topRight={<>
          <div style={{ position: "relative" }}>
            <ButtonGhost icon={<svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 5a4 4 0 018 0v3l1 2H2l1-2V5z M5 11a2 2 0 004 0" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}>
              Inbox
            </ButtonGhost>
            {notifShow && (
              <div className="tep-pulse" style={{
                position: "absolute", top: 4, right: 6,
                width: 8, height: 8, borderRadius: 8, background: "oklch(60% 0.18 25)",
              }}/>
            )}
          </div>
          <ButtonPrimary>+ New student</ButtonPrimary>
        </>}>
        <div style={{ padding: "16px 24px", overflow: "hidden", height: "100%", position: "relative" }}>
          {/* Student list (background) */}
          <StudentList highlight="Mateo Ramírez" cellBg={panelOpen ? "#fbf8ee" : "#fff"}/>

          {/* Notification toast */}
          {notifShow && (
            <div style={{
              position: "absolute", right: 28, top: 10,
              background: "#fff", borderRadius: 12, border: "1px solid #d8d2bd",
              padding: "12px 14px", boxShadow: "0 18px 40px -12px rgba(0,0,0,0.25)",
              display: "flex", gap: 10, alignItems: "center",
              opacity: t > 1.3 ? Math.max(0, 1 - (t - 1.3) / 0.2) : Math.min(1, (t - 0.2) / 0.3),
              transform: `translateY(${(1 - Math.min(1, (t - 0.2) / 0.3)) * -12}px)`,
              zIndex: 30, width: 280,
            }}>
              <div style={{ width: 32, height: 32, borderRadius: 32, background: "#e6ede0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16"><path d="M3 9l3 3 7-8" stroke="#1f3d2b" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>Placement results in</div>
                <div style={{ fontSize: 11.5, color: "#6b7269" }}>Mateo Ramírez · just now</div>
              </div>
            </div>
          )}

          {/* Results panel (slides from right) */}
          {panelOpen && (
            <div style={{
              position: "absolute", right: 0, top: 0, bottom: 0,
              width: 600, background: "#fff",
              borderLeft: "1px solid #d8d2bd",
              boxShadow: "-30px 0 60px -20px rgba(0,0,0,0.2)",
              transform: `translateX(${(1 - panelP) * 100}%)`,
              transition: "transform 60ms",
              padding: 28, overflow: "hidden",
            }}>
              <ResultsPanel t={t}/>
            </div>
          )}

          <Cursor x={cursor.x} y={cursor.y} label={t > 1.0 && t < 1.4 ? "open" : null}/>
        </div>
      </AppShell>
    </div>
  );
}

function StudentList({ highlight, cellBg = "#fff" }) {
  const rows = [
    { name: "Yuki Tanaka", country: "JP", level: "B2", progress: 67, lessons: "Wk 31 / 52" },
    { name: "Mateo Ramírez", country: "ES", level: "—", progress: 0, lessons: "Awaiting result", flash: true },
    { name: "Aylin Demir", country: "TR", level: "A2", progress: 28, lessons: "Wk 14 / 52" },
    { name: "Lucas Almeida", country: "BR", level: "B1", progress: 51, lessons: "Wk 26 / 52" },
    { name: "Hyejin Park", country: "KR", level: "C1", progress: 84, lessons: "Wk 43 / 52" },
    { name: "Linh Nguyen", country: "VN", level: "B1", progress: 39, lessons: "Wk 19 / 52" },
  ];
  return (
    <div style={{
      background: cellBg, border: "1px solid #e8e3d2", borderRadius: 12,
      overflow: "hidden", marginTop: 6,
    }}>
      <div style={{
        display: "grid", gridTemplateColumns: "1.6fr 0.6fr 1fr 1fr 0.8fr",
        padding: "10px 18px", borderBottom: "1px solid #e8e3d2",
        fontSize: 10.5, color: "#8a8f86", fontFamily: "var(--mono)",
        textTransform: "uppercase", letterSpacing: "0.08em",
      }}>
        <div>Student</div><div>CEFR</div><div>Pathway</div><div>Progress</div><div style={{textAlign:"right"}}>Last seen</div>
      </div>
      {rows.map((r, i) => (
        <div key={r.name} style={{
          display: "grid", gridTemplateColumns: "1.6fr 0.6fr 1fr 1fr 0.8fr",
          padding: "12px 18px", alignItems: "center",
          borderBottom: i < rows.length - 1 ? "1px solid #f3eedf" : "none",
          background: r.name === highlight ? "#f5f0db" : "transparent",
          fontSize: 13,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar name={r.name} color={["#7a8c5a","#4a6b52","#8c6e4a","#5e8c7a","#6e5a8c","#a07a3e"][i]} size={28}/>
            <div>
              <div style={{ fontWeight: 600 }}>{r.name}</div>
              <div style={{ fontSize: 11, color: "#8a8f86", display: "flex", alignItems: "center", gap: 6 }}>
                <FlagDot country={r.country} size={9}/> {r.lessons}
              </div>
            </div>
          </div>
          <div>
            {r.level === "—"
              ? <Chip color="tan" size="sm">{r.flash ? "Result in!" : "Pending"}</Chip>
              : <Chip color="sage" size="sm">{r.level}</Chip>}
          </div>
          <div style={{ fontSize: 12, color: "#6b7269" }}>{r.lessons}</div>
          <div style={{ paddingRight: 24 }}>
            <ProgressBar value={r.progress} color="#1f3d2b" track="#e8e3d2" height={5}/>
          </div>
          <div style={{ textAlign: "right", color: "#8a8f86", fontFamily: "var(--mono)", fontSize: 11 }}>
            {["1d ago","just now","3h ago","2d ago","1d ago","4h ago"][i]}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResultsPanel({ t }) {
  // t is global Stage time, 0-5
  const localT = Math.max(0, t - 1.4);
  const skills = [
    { name: "Listening", value: 68, color: "#1f3d2b" },
    { name: "Reading",   value: 73, color: "#2f5740" },
    { name: "Grammar",   value: 70, color: "#4a6b52" },
    { name: "Vocabulary",value: 64, color: "#7a8c5a" },
    { name: "Writing",   value: 58, color: "#a07a3e" },
  ];
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Avatar name="Mateo Ramírez" color={STUDENT.color} size={40}/>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "var(--display)", fontSize: 18, fontWeight: 500 }}>Mateo Ramírez</div>
          <div style={{ fontSize: 11.5, color: "#8a8f86", display: "flex", gap: 6, alignItems: "center" }}>
            <FlagDot country="ES" size={9}/> Madrid · placement complete · 24m 08s
          </div>
        </div>
        <Chip color="sage" size="sm">● new</Chip>
      </div>

      {/* CEFR */}
      <div style={{
        marginTop: 22, padding: 18,
        background: "#1f3d2b", borderRadius: 14, color: "#faf6ed",
        display: "flex", alignItems: "center", gap: 18,
        opacity: Math.min(1, Math.max(0, (localT - 0.5) / 0.4)),
        transform: `translateY(${Math.max(0, (1 - Math.min(1, (localT - 0.5) / 0.4)) * 12)}px)`,
      }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 56, fontWeight: 500, lineHeight: 1, letterSpacing: "-0.02em" }}>
          B1
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontFamily: "var(--mono)", letterSpacing: "0.08em", textTransform: "uppercase", color: "#c7b89a" }}>CEFR placement</div>
          <div style={{ fontSize: 16, fontWeight: 500, marginTop: 2 }}>Intermediate · lower band</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#c7b89a" }}>Confidence</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>92%</div>
        </div>
      </div>

      {/* Skill bars */}
      <div style={{ marginTop: 18, padding: 0 }}>
        <Label>Skill breakdown</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {skills.map((s, i) => {
            const ts = 2.0 + i * 0.18;
            const p = Math.min(1, Math.max(0, (t - ts) / 0.6));
            return (
              <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 90, fontSize: 12.5, fontWeight: 500 }}>{s.name}</div>
                <div style={{ flex: 1, position: "relative" }}>
                  <div style={{ height: 8, background: "#e8e3d2", borderRadius: 4 }}/>
                  <div style={{
                    position: "absolute", left: 0, top: 0,
                    width: `${s.value * p}%`, height: 8,
                    background: s.color, borderRadius: 4,
                  }}/>
                </div>
                <div style={{ width: 36, fontFamily: "var(--mono)", fontSize: 12, textAlign: "right" }}>
                  {Math.round(s.value * p)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tutor notes auto-generated */}
      <div style={{
        marginTop: 18, padding: 14,
        background: "#faf6ed", border: "1px solid #e8e3d2", borderRadius: 10,
        opacity: Math.min(1, Math.max(0, (t - 3.6) / 0.4)),
      }}>
        <Label>Tutor brief · generated</Label>
        <div style={{ fontSize: 12.5, color: "#2c322d", lineHeight: 1.55, marginTop: 4 }}>
          Solid receptive skills. Production lags — particularly written tense control (present perfect, past simple/continuous contrast). Vocabulary range is workplace‑adjacent but informal register dominates.
        </div>
      </div>

      <div style={{ marginTop: "auto", paddingTop: 16, display: "flex", gap: 8 }}>
        <ButtonGhost style={{ flex: 1, justifyContent: "center", textAlign: "center" }}>View full report</ButtonGhost>
        <ButtonPrimary style={{ flex: 2, justifyContent: "center", textAlign: "center" }}>Continue → goals & interests</ButtonPrimary>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// SCENE 4: Add interests + goals
// ────────────────────────────────────────────────────────────────────
function Scene4() {
  const t = useTime();
  const interests = [
    { label: "Architecture", t: 0.4 },
    { label: "Travel", t: 0.7 },
    { label: "Cycling", t: 1.0 },
    { label: "Cooking", t: 1.3 },
    { label: "Football", t: 1.6 },
    { label: "Sci‑fi novels", t: 1.9 },
  ];
  const goals = [
    { label: "Lead site meetings with international clients", t: 2.5 },
    { label: "Write project proposals in English", t: 3.0 },
    { label: "Pass IELTS 6.5 by autumn", t: 3.5 },
    { label: "Watch films without subtitles", t: 4.0 },
  ];
  return (
    <div style={{ width: 1280, height: 720, background: "#ece6d4", position: "relative" }}>
      <AppShell active="students" breadcrumb="STUDENTS / MATEO RAMÍREZ / ONBOARDING" title="Build the learner profile"
        topRight={<><ButtonGhost>Back</ButtonGhost><ButtonPrimary>Generate pathway →</ButtonPrimary></>}>
        <div style={{ padding: "20px 28px", display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 24, height: "100%" }}>
          {/* Left: profile recap */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: "#fff", border: "1px solid #e8e3d2", borderRadius: 14, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <Avatar name="Mateo Ramírez" color={STUDENT.color} size={44} ring="#e6ede0"/>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "var(--display)", fontSize: 18, fontWeight: 500 }}>Mateo Ramírez</div>
                  <div style={{ fontSize: 11.5, color: "#8a8f86", display: "flex", gap: 6, alignItems: "center" }}>
                    <FlagDot country="ES" size={9}/> Madrid · 28 · Architect at Estudio Norte
                  </div>
                </div>
                <Chip color="sage">B1</Chip>
              </div>
              <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {[
                  ["Listening","68"],["Reading","73"],["Grammar","70"],
                  ["Vocab","64"],["Writing","58"],["Overall","B1.1"],
                ].map(([k,v],i) => (
                  <div key={i} style={{ background: "#faf6ed", border: "1px solid #e8e3d2", borderRadius: 8, padding: "8px 10px" }}>
                    <div style={{ fontSize: 10, color: "#8a8f86", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{k}</div>
                    <div style={{ fontFamily: "var(--display)", fontSize: 18, fontWeight: 500 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e8e3d2", borderRadius: 14, padding: 18, flex: 1 }}>
              <Label>Tutor brief</Label>
              <div style={{ fontSize: 13, color: "#2c322d", lineHeight: 1.55 }}>
                Mateo has a Spanish‑Architecture background. Goals lean professional (international clients, IELTS) — recommend a workplace‑English skew through months 4–8. Spanish L1 means he&rsquo;ll struggle with present perfect, phrasal verbs and prepositions of time. He&rsquo;s listening‑strong but writing‑weak: prioritise productive practice.
              </div>
            </div>
          </div>

          {/* Right: form filling in */}
          <div style={{ background: "#fff", border: "1px solid #e8e3d2", borderRadius: 14, padding: 22, position: "relative", overflow: "hidden" }}>
            <Label>Step 2 of 3 · Interests &amp; goals</Label>
            <div style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 500, marginTop: 4 }}>What does Mateo care about?</div>
            <div style={{ color: "#6b7269", fontSize: 12.5, marginTop: 4 }}>The pathway will weave these into reading passages, role‑plays and lesson topics.</div>

            <div style={{ marginTop: 18 }}>
              <Label>Interests</Label>
              <div style={{
                minHeight: 64, padding: 10,
                border: "1px solid #d8d2bd", borderRadius: 10,
                display: "flex", flexWrap: "wrap", gap: 6, alignContent: "flex-start",
              }}>
                {interests.map(i => t > i.t && (
                  <div key={i.label} style={{
                    transform: `scale(${interpolate([0,0.3], [0.6, 1], Easing.easeOutBack)(Math.min(0.3, t - i.t))})`,
                    transformOrigin: "center",
                  }}>
                    <Chip color="sage" size="md">{i.label} ✕</Chip>
                  </div>
                ))}
                {t < 2.0 && <span className="tep-caret" style={{ color: "#1f3d2b", fontSize: 18 }}>|</span>}
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <Label>Learning goals</Label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {goals.map(g => {
                  const visible = t > g.t;
                  const checked = t > g.t + 0.3;
                  return (
                    <div key={g.label} style={{
                      opacity: visible ? 1 : 0,
                      transform: `translateX(${visible ? 0 : -8}px)`,
                      transition: "opacity 200ms, transform 200ms",
                      display: "flex", alignItems: "center", gap: 10,
                      border: "1px solid #d8d2bd", borderRadius: 8, padding: "9px 12px",
                      background: checked ? "#f5f0db" : "#fff",
                    }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 4,
                        border: `1.5px solid ${checked ? "#1f3d2b" : "#d8d2bd"}`,
                        background: checked ? "#1f3d2b" : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {checked && <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2 2 4-5" stroke="#faf6ed" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                      <div style={{ fontSize: 12.5 }}>{g.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <Label>Sessions per week</Label>
              <div style={{ display: "flex", gap: 6 }}>
                {[1,2,3,4].map(n => (
                  <div key={n} style={{
                    flex: 1, textAlign: "center", padding: "10px 0",
                    border: `1.5px solid ${n === 1 ? "#1f3d2b" : "#d8d2bd"}`,
                    borderRadius: 8, fontSize: 13, fontWeight: n === 1 ? 600 : 500,
                    background: n === 1 ? "#e6ede0" : "#fff",
                    color: n === 1 ? "#1f3d2b" : "#2c322d",
                  }}>{n}× /wk</div>
                ))}
              </div>
            </div>

            {/* Save flash */}
            {t > 4.4 && (
              <div style={{
                position: "absolute", left: 0, right: 0, bottom: 0,
                background: "#1f3d2b", color: "#faf6ed",
                padding: "14px 22px", display: "flex", alignItems: "center", gap: 12,
                opacity: Math.min(1, (t - 4.4) / 0.3),
              }}>
                <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 7l3 3 5-6" stroke="#c7b89a" strokeWidth="1.8" fill="none" strokeLinejoin="round" strokeLinecap="round"/></svg>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Profile saved — ready to generate pathway</div>
              </div>
            )}
          </div>
        </div>
      </AppShell>
    </div>
  );
}

export { Scene1, Scene2, Scene3, Scene4 };
